import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { money } from "@/lib/api";
import { useLiveCallsCount } from "@/hooks/useLiveCallsCount";
import { can, firstAllowedPath, isPlatformOperator } from "@/lib/permissions";
import { PageHeader, StatCard } from "@/components/common";
import {
  UsersThree, Storefront, Megaphone, Wallet, ShareNetwork, WaveSine, StackSimple,
  UserCircle, Broadcast, ChartLineUp, Phone, ListBullets, Headset, Pause,
  Minus, Plus,
} from "@phosphor-icons/react";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";

const PALETTE = ["#8b5cf6", "#10b981", "#f59e0b", "#0ea5e9", "#f43f5e", "#64748b", "#ec4899"];

const fmtDay = (d) => {
  const dt = new Date(`${d}T00:00:00`);
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};
const prettyLabel = (s) =>
  String(s || "unknown").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const fmtDuration = (sec) => {
  const n = Math.max(0, Math.floor(Number(sec) || 0));
  const h = Math.floor(n / 3600);
  const m = Math.floor((n % 3600) / 60);
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  if (m) return `${m}m`;
  return `${n}s`;
};

function LiveCallsTile() {
  const count = useLiveCallsCount();
  return (
    <Link to="/live-calls" className="block focus:outline-none focus:ring-2 focus:ring-primary rounded-xl" data-testid="dashboard-live-calls-tile">
      <StatCard testid="stat-live-calls" icon={Broadcast} label="Live Calls"
        value={count} hint={count > 0 ? "Connected right now — click to view" : "No calls connected"}
        tone={count > 0 ? "success" : "muted"} />
    </Link>
  );
}

function ChartCard({ title, subtitle, testid, children, empty, tall }) {
  const h = tall ? "h-[320px]" : "h-[260px]";
  return (
    <div className="rounded-xl border border-border bg-card card-shadow p-5 sm:p-6" data-testid={testid}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="text-sm font-semibold">{title}</h3>
          {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {empty ? (
        <div className={`${h} grid place-items-center text-center`} data-testid={`${testid}-empty`}>
          <div className="text-muted-foreground">
            <ChartLineUp className="w-8 h-8 mx-auto mb-2 opacity-50" weight="duotone" />
            <p className="text-sm">No data yet</p>
          </div>
        </div>
      ) : (
        <div className={h} style={{ fontSize: 12 }}>{children}</div>
      )}
    </div>
  );
}

const tooltipStyle = {
  background: "hsl(var(--popover))",
  border: "1px solid hsl(var(--border))",
  borderRadius: 10,
  fontSize: 12,
  color: "hsl(var(--popover-foreground))",
};
const axis = { stroke: "hsl(var(--muted-foreground))", fontSize: 11 };

function SignupsChart({ data, showResellers }) {
  const empty = !data || data.every((d) => (d.customers || 0) + (d.resellers || 0) === 0);
  return (
    <ChartCard testid="chart-signups" title={showResellers ? "New signups" : "My new customer signups"}
      subtitle="Last 30 days" empty={empty}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
          <defs>
            <linearGradient id="gc" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.5} />
              <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gr" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#10b981" stopOpacity={0.5} />
              <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
          <XAxis dataKey="date" tickFormatter={fmtDay} tick={axis} minTickGap={24} axisLine={false} tickLine={false} />
          <YAxis allowDecimals={false} tick={axis} axisLine={false} tickLine={false} width={34} />
          <Tooltip contentStyle={tooltipStyle} labelFormatter={fmtDay} />
          {showResellers && <Legend wrapperStyle={{ fontSize: 12 }} />}
          <Area type="monotone" dataKey="customers" name="Customers" stroke="#8b5cf6" strokeWidth={2} fill="url(#gc)" />
          {showResellers && <Area type="monotone" dataKey="resellers" name="Resellers" stroke="#10b981" strokeWidth={2} fill="url(#gr)" />}
        </AreaChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

function CallsChart({
  data,
  title = "Calls over time",
  subtitle = "Last 14 days",
  testid = "chart-calls",
  color = "#0ea5e9",
  seriesName = "Calls",
}) {
  const empty = !data || data.every((d) => (d.calls || 0) === 0);
  const gid = `g-${testid}`;
  return (
    <ChartCard testid={testid} title={title} subtitle={subtitle} empty={empty}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
          <defs>
            <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.5} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
          <XAxis dataKey="date" tickFormatter={fmtDay} tick={axis} minTickGap={24} axisLine={false} tickLine={false} />
          <YAxis allowDecimals={false} tick={axis} axisLine={false} tickLine={false} width={34} />
          <Tooltip contentStyle={tooltipStyle} labelFormatter={fmtDay} />
          <Area type="monotone" dataKey="calls" name={seriesName} stroke={color} strokeWidth={2} fill={`url(#${gid})`} />
        </AreaChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

const INCOMING_SERIES = [
  { key: "answered", name: "Answered", color: "#10b981" },
  { key: "missed", name: "Missed", color: "#f43f5e" },
  { key: "abandoned", name: "Abandoned", color: "#f59e0b" },
  { key: "voicemail", name: "Voicemail", color: "#8b5cf6" },
  { key: "failed", name: "Failed", color: "#64748b" },
  { key: "queued", name: "Queued", color: "#0ea5e9" },
  { key: "other", name: "Other", color: "#94a3b8" },
];

function IncomingCallsChart({ data }) {
  const empty = !data || data.every((d) => (d.calls || 0) === 0);
  const active = INCOMING_SERIES.filter((s) => data?.some((d) => (d[s.key] || 0) > 0));
  const series = active.length ? active : INCOMING_SERIES.slice(0, 3);
  return (
    <ChartCard
      testid="chart-incoming-calls"
      title="Incoming calls"
      subtitle="DID inbound · last 14 days · answered / missed / abandoned"
      empty={empty}
      tall
    >
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
          <defs>
            {series.map((s) => (
              <linearGradient key={s.key} id={`gin-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={s.color} stopOpacity={0.45} />
                <stop offset="100%" stopColor={s.color} stopOpacity={0} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
          <XAxis dataKey="date" tickFormatter={fmtDay} tick={axis} minTickGap={24} axisLine={false} tickLine={false} />
          <YAxis allowDecimals={false} tick={axis} axisLine={false} tickLine={false} width={34} />
          <Tooltip contentStyle={tooltipStyle} labelFormatter={fmtDay} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {series.map((s) => (
            <Area
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.name}
              stackId="incoming"
              stroke={s.color}
              strokeWidth={2}
              fill={`url(#gin-${s.key})`}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

function TopCustomersChart({ data }) {
  const empty = !data || data.length === 0;
  return (
    <ChartCard testid="chart-top-customers" title="Top customers by calls" subtitle="Today" empty={empty}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 12, left: 8, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
          <XAxis type="number" allowDecimals={false} tick={axis} axisLine={false} tickLine={false} />
          <YAxis type="category" dataKey="name" tick={axis} width={110} axisLine={false} tickLine={false} />
          <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "hsl(var(--muted))", opacity: 0.4 }} />
          <Bar dataKey="calls" name="Calls" radius={[0, 6, 6, 0]}>
            {data?.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

function RankTable({ title, subtitle, testid, empty, href, children }) {
  return (
    <div className="rounded-xl border border-border bg-card card-shadow p-5 sm:p-6" data-testid={testid}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="text-sm font-semibold">{title}</h3>
          {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
        </div>
        {href && (
          <Link to={href} className="text-xs text-sky-700 hover:underline shrink-0">View all</Link>
        )}
      </div>
      {empty ? (
        <div className="h-[180px] grid place-items-center text-center" data-testid={`${testid}-empty`}>
          <div className="text-muted-foreground">
            <ChartLineUp className="w-8 h-8 mx-auto mb-2 opacity-50" weight="duotone" />
            <p className="text-sm">No data yet</p>
          </div>
        </div>
      ) : children}
    </div>
  );
}

function UsedListsCard({ rows }) {
  const list = rows || [];
  return (
    <RankTable
      testid="dash-used-lists"
      title="Lists in use"
      subtitle="Active campaign lists · poor quality and bad-lead counts"
      href="/lists"
      empty={!list.length}
    >
      <ul className="space-y-2.5" data-testid="dash-used-lists-rows">
        {list.map((r) => (
          <li key={r.id} className="flex items-start justify-between gap-3 text-sm" data-testid="dash-used-list-row">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                {r.sign === "negative" ? (
                  <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-red-100 text-red-700" title="Poor">
                    <Minus className="w-3 h-3" weight="bold" />
                  </span>
                ) : r.sign === "positive" ? (
                  <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-100 text-emerald-700" title="Good">
                    <Plus className="w-3 h-3" weight="bold" />
                  </span>
                ) : (
                  <ListBullets className="w-4 h-4 text-muted-foreground" />
                )}
                <span className="font-medium truncate">{r.name}</span>
              </div>
              <p className="text-[11px] text-muted-foreground mt-0.5 truncate">
                {(r.campaigns || []).join(", ") || "—"}
                {r.attempted ? ` · ${r.attempted} dialed` : ""}
              </p>
            </div>
            <div className="text-right shrink-0">
              <p className={r.sign === "negative" ? "text-xs font-semibold text-red-600" : "text-xs text-muted-foreground"}>
                {r.label}{r.score != null ? ` · ${r.score}` : ""}
              </p>
              <p className="text-[11px] text-muted-foreground">{r.bad_leads || 0} bad leads</p>
            </div>
          </li>
        ))}
      </ul>
    </RankTable>
  );
}

function TopAgentsCard({ rows }) {
  const list = rows || [];
  return (
    <RankTable
      testid="dash-top-agents"
      title="Top agents"
      subtitle="Calls taken today"
      href="/reports/live-stats"
      empty={!list.length}
    >
      <ResponsiveContainer width="100%" height={Math.max(160, list.length * 36)}>
        <BarChart data={list} layout="vertical" margin={{ top: 4, right: 12, left: 8, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
          <XAxis type="number" allowDecimals={false} tick={axis} axisLine={false} tickLine={false} />
          <YAxis type="category" dataKey="name" tick={axis} width={110} axisLine={false} tickLine={false} />
          <Tooltip
            contentStyle={tooltipStyle}
            cursor={{ fill: "hsl(var(--muted))", opacity: 0.4 }}
            formatter={(v, name) => [name === "talk_sec" ? fmtDuration(v) : v, name === "talk_sec" ? "Talk" : "Calls"]}
          />
          <Bar dataKey="calls" name="Calls" radius={[0, 6, 6, 0]}>
            {list.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <ul className="mt-3 space-y-1.5 text-[11px] text-muted-foreground">
        {list.map((r) => (
          <li key={r.id} className="flex justify-between gap-2" data-testid="dash-top-agent-row">
            <span className="flex items-center gap-1.5 truncate">
              <Headset className="w-3.5 h-3.5" />
              {r.name}
            </span>
            <span>{r.calls} calls · {fmtDuration(r.talk_sec)}</span>
          </li>
        ))}
      </ul>
    </RankTable>
  );
}

function AgentBreaksCard({ rows }) {
  const list = rows || [];
  return (
    <RankTable
      testid="dash-agent-breaks"
      title="Agent breaks"
      subtitle="Not Ready time today"
      href="/reports/live-stats"
      empty={!list.length}
    >
      <ul className="space-y-2.5" data-testid="dash-agent-breaks-rows">
        {list.map((r) => (
          <li key={r.id} className="flex items-center justify-between gap-3 text-sm" data-testid="dash-agent-break-row">
            <div className="min-w-0">
              <p className="font-medium truncate">{r.name}</p>
              <p className="text-[11px] text-muted-foreground">
                {r.breaks} break{r.breaks === 1 ? "" : "s"}
                {r.on_break ? " · on break now" : ""}
              </p>
            </div>
            <div className="flex items-center gap-1.5 text-xs font-semibold shrink-0">
              <Pause className="w-3.5 h-3.5 text-amber-600" weight="fill" />
              {fmtDuration(r.pause_sec)}
            </div>
          </li>
        ))}
      </ul>
    </RankTable>
  );
}

function DonutChart({ title, subtitle, testid, data }) {
  const rows = (data || []).map((d) => ({ name: prettyLabel(d.name), value: d.value }));
  const empty = rows.length === 0 || rows.every((d) => d.value === 0);
  return (
    <ChartCard testid={testid} title={title} subtitle={subtitle} empty={empty}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={rows} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
            {rows.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
          </Pie>
          <Tooltip contentStyle={tooltipStyle} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
        </PieChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [dash, setDash] = useState(null);

  const platform = isPlatformOperator(user);

  useEffect(() => {
    if (user?.role === "staff" && !can(user, "dashboard", "view")) return;
    api.get("/stats/summary").then((r) => setStats(r.data)).catch(() => {});
    api.get("/stats/dashboard").then((r) => setDash(r.data)).catch(() => {});
    if (!platform)
      api.get("/wallets/me").then((r) => setWallet(r.data)).catch(() => {});
  }, [user, platform]);

  if (user?.role === "staff" && !can(user, "dashboard", "view")) {
    return <Navigate to={firstAllowedPath(user)} replace />;
  }

  const greeting = {
    admin: "Platform overview",
    staff: "Platform overview",
    reseller: "Reseller overview",
    customer: "Account overview",
  }[user?.role];
  const role = user?.role;

  return (
    <div>
      <PageHeader title={`Welcome, ${user?.name?.split(" ")[0] || "there"}`}
        subtitle={`${greeting} · strict tenant-isolated data`} />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {platform && <>
          <LiveCallsTile />
          <StatCard testid="stat-resellers" icon={Storefront} label="Resellers" value={stats?.resellers ?? "—"} tone="primary" />
          <StatCard testid="stat-customers" icon={UsersThree} label="Customers" value={stats?.customers ?? "—"}
            hint={`${stats?.direct_customers ?? 0} direct · ${stats?.reseller_customers ?? 0} via resellers`} tone="success" />
          <StatCard testid="stat-opensips" icon={ShareNetwork} label="OpenSIPS Servers" value={stats?.opensips_servers ?? "—"} tone="warning" />
          <StatCard testid="stat-media" icon={WaveSine} label="Media Servers" value={stats?.media_servers ?? "—"} tone="muted" />
          <StatCard testid="stat-campaigns" icon={Megaphone} label="Global Campaigns" value={stats?.campaigns ?? "—"} tone="primary" />
          <StatCard testid="stat-reservations" icon={StackSimple} label="Active Holds" value={stats?.active_reservations ?? "—"} tone="warning" />
          <StatCard testid="stat-direct" icon={UserCircle} label="Direct Customers" value={stats?.direct_customers ?? "—"} tone="success" />
        </>}

        {role === "reseller" && <>
          <LiveCallsTile />
          <StatCard testid="stat-customers" icon={UsersThree} label="My Customers" value={stats?.customers ?? "—"} tone="success" />
          <StatCard testid="stat-campaigns" icon={Megaphone} label="Campaigns" value={stats?.campaigns ?? "—"} tone="warning" />
          {wallet && <StatCard testid="stat-wallet" icon={Wallet} label="Available Balance"
            value={money(wallet.available_minor)} tone="success" />}
        </>}

        {role === "customer" && <>
          <LiveCallsTile />
          <StatCard testid="stat-campaigns" icon={Megaphone} label="My Campaigns" value={stats?.campaigns ?? "—"} tone="warning" />
          <StatCard testid="stat-dids" icon={Phone} label="DIDs" value={stats?.dids ?? "—"}
            hint={`${stats?.inbound_groups ?? 0} inbound groups`} tone="success" />
          {wallet && <StatCard testid="stat-wallet" icon={Wallet} label="Available Balance"
            value={money(wallet.available_minor)} tone="success" />}
        </>}
      </div>

      {/* Detailed analytics */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="dashboard-charts">
        {platform && <>
          <SignupsChart data={dash?.signups_series} showResellers />
          <CallsChart data={dash?.calls_series} />
          <TopCustomersChart data={dash?.top_customers_today} />
          <DonutChart testid="chart-transfer-outcomes" title="Transfer outcomes" subtitle="Today"
            data={dash?.transfer_outcomes_today} />
        </>}

        {role === "reseller" && <>
          <SignupsChart data={dash?.signups_series} showResellers={false} />
          <CallsChart data={dash?.calls_series} />
          <TopCustomersChart data={dash?.top_customers_today} />
        </>}

        {role === "customer" && <>
          <div className="lg:col-span-2">
            <IncomingCallsChart data={dash?.incoming_calls_series} />
          </div>
          <CallsChart data={dash?.calls_series} />
          <DonutChart testid="chart-transfer-outcomes" title="Transfer outcomes" subtitle="Today"
            data={dash?.transfer_outcomes_today} />
          <DonutChart testid="chart-dispositions" title="Call dispositions" subtitle="Today"
            data={dash?.dispositions_today} />
          <UsedListsCard rows={dash?.used_lists_quality} />
          <TopAgentsCard rows={dash?.top_agents_today} />
          <div className="lg:col-span-2">
            <AgentBreaksCard rows={dash?.agent_breaks_today} />
          </div>
        </>}
      </div>
    </div>
  );
}
